import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { EnterpriseDiagnosticAnswer, DimensionId, SubDimensionScore } from '@/data/enterpriseDiagnosticData';
import { buildRubricContext } from '@/data/enterpriseDiagnosticData';
import type { RubricsOverride } from '@/components/admin/config/DiagnosticRubricsEditor';

/**
 * Lista de diagnósticos COMPLETADOS de la organización.
 * Filtra drafts (`completed_at IS NULL`) para que no aparezcan como resultados.
 */
export function useEnterpriseDiagnostics(organizationId?: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['enterprise-diagnostics', organizationId],
    queryFn: async () => {
      let query = supabase
        .from('enterprise_diagnostics')
        .select('*')
        .not('completed_at', 'is', null)          // solo completados
        .order('completed_at', { ascending: false });

      if (organizationId && organizationId !== 'all') {
        query = query.eq('organization_id', organizationId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
}

export function useLatestEnterpriseDiagnostic(organizationId?: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['enterprise-diagnostic-latest', organizationId],
    queryFn: async () => {
      let query = supabase
        .from('enterprise_diagnostics')
        .select('*')
        .not('completed_at', 'is', null)          // solo completados
        .order('completed_at', { ascending: false })
        .limit(1);

      if (organizationId && organizationId !== 'all') {
        query = query.eq('organization_id', organizationId);
      }

      const { data, error } = await query.maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
}

interface SaveDiagnosticParams {
  organizationId: string;
  companyName: string;
  sector: string;
  employeeCount: string;
  answers: Record<string, EnterpriseDiagnosticAnswer>;
  overallLevel: number;
  overallLevelName: string;
  dimensionScores: any;
  completionPercentage: number;
}

/**
 * Cuando el diagnóstico se COMPLETA: si hay draft server-side del mismo user+org,
 * lo actualizamos (upgrade a completed_at = now). Si no hay draft, insertamos nuevo.
 */
export function useSaveEnterpriseDiagnostic() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: SaveDiagnosticParams) => {
      if (!user) throw new Error('Not authenticated');

      // Buscar si hay draft previo del user en esta org (completed_at IS NULL)
      const { data: existingDraft } = await supabase
        .from('enterprise_diagnostics')
        .select('id')
        .eq('organization_id', params.organizationId)
        .eq('assessed_by', user.id)
        .is('completed_at', null)
        .maybeSingle();

      const payload = {
        organization_id: params.organizationId,
        assessed_by: user.id,
        company_name: params.companyName,
        sector: params.sector,
        employee_count: params.employeeCount,
        answers: params.answers as any,
        overall_level: params.overallLevel,
        overall_level_name: params.overallLevelName,
        dimension_scores: params.dimensionScores as any,
        completion_percentage: params.completionPercentage,
        completed_at: new Date().toISOString(),
      };

      let data;
      if (existingDraft?.id) {
        // Upgrade del draft a completado
        const res = await supabase
          .from('enterprise_diagnostics')
          .update(payload)
          .eq('id', existingDraft.id)
          .select()
          .single();
        if (res.error) throw res.error;
        data = res.data;
      } else {
        // No hay draft, insertar nuevo
        const res = await supabase
          .from('enterprise_diagnostics')
          .insert(payload)
          .select()
          .single();
        if (res.error) throw res.error;
        data = res.data;
      }

      // Sync employee count to organization if not already set
      try {
        const rangeMap: Record<string, number> = {
          '1-10': 10, '11-50': 50, '51-200': 200,
          '201-500': 500, '501-1000': 1000, '1000+': 1000,
        };
        const numeric = rangeMap[params.employeeCount];
        if (numeric && params.organizationId) {
          const { data: org } = await supabase
            .from('organizations')
            .select('employee_count')
            .eq('id', params.organizationId)
            .maybeSingle();
          if (!org?.employee_count) {
            await supabase
              .from('organizations')
              .update({ employee_count: numeric })
              .eq('id', params.organizationId);
          }
        }
      } catch (e) {
        console.warn('Could not sync employee_count to organization:', e);
      }

      // Aviso interno (no bloquea el guardado si falla)
      try {
        if (data?.id) {
          await supabase.functions.invoke('register-enterprise', {
            body: { action: 'diagnostic_completed', diagnostic_id: data.id },
          });
        }
      } catch (e) {
        console.warn('No se pudo enviar el aviso interno del diagnóstico:', e);
      }

      return data;
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['enterprise-diagnostics'] });
      queryClient.invalidateQueries({ queryKey: ['enterprise-diagnostic-latest'] });
      queryClient.invalidateQueries({ queryKey: ['enterprise-diagnostic-draft'] });
      queryClient.invalidateQueries({ queryKey: ['org-settings', params.organizationId] });
    },
  });
}

/**
 * Draft server-side: busca el draft del user actual en la org indicada.
 * Devuelve null si no hay draft. Enabled solo con user + orgId válido.
 */
export interface EnterpriseDraftServer {
  id: string;
  organization_id: string;
  assessed_by: string;
  company_name: string | null;
  sector: string | null;
  employee_count: string | null;
  answers: Record<string, EnterpriseDiagnosticAnswer> | null;
  completion_percentage: number | null;
  updated_at: string;
}

export function useLatestEnterpriseDraft(organizationId?: string | null) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['enterprise-diagnostic-draft', organizationId, user?.id],
    queryFn: async () => {
      if (!user || !organizationId || organizationId === 'all') return null;
      const { data, error } = await supabase
        .from('enterprise_diagnostics')
        .select('id, organization_id, assessed_by, company_name, sector, employee_count, answers, completion_percentage, updated_at')
        .eq('organization_id', organizationId)
        .eq('assessed_by', user.id)
        .is('completed_at', null)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as unknown as EnterpriseDraftServer | null;
    },
    enabled: !!user && !!organizationId && organizationId !== 'all',
    staleTime: 30_000,
  });
}

interface SaveDraftParams {
  organizationId: string;
  companyName?: string | null;
  sector?: string | null;
  employeeCount?: string | null;
  answers?: Record<string, EnterpriseDiagnosticAnswer> | null;
  completionPercentage?: number | null;
}

/**
 * UPSERT del draft server-side. Se llama en un intervalo (autosave debounced).
 * El unique index parcial `uniq_enterprise_diag_draft_per_user_org` garantiza
 * máximo 1 draft por (org, user).
 */
export function useSaveEnterpriseDraft() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: SaveDraftParams) => {
      if (!user) throw new Error('Not authenticated');

      const { data: existing } = await supabase
        .from('enterprise_diagnostics')
        .select('id')
        .eq('organization_id', params.organizationId)
        .eq('assessed_by', user.id)
        .is('completed_at', null)
        .maybeSingle();

      const payload: any = {
        organization_id: params.organizationId,
        assessed_by: user.id,
        company_name: params.companyName ?? null,
        sector: params.sector ?? null,
        employee_count: params.employeeCount ?? null,
        answers: (params.answers ?? {}) as any,
        completion_percentage: params.completionPercentage ?? 0,
        completed_at: null,
      };

      if (existing?.id) {
        const { error } = await supabase
          .from('enterprise_diagnostics')
          .update(payload)
          .eq('id', existing.id);
        if (error) throw error;
        return existing.id;
      } else {
        const { data, error } = await supabase
          .from('enterprise_diagnostics')
          .insert(payload)
          .select('id')
          .single();
        if (error) throw error;
        return data.id;
      }
    },
    onError: (error) => {
      console.error('[enterprise-draft] autosave failed:', error);
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['enterprise-diagnostic-draft', params.organizationId] });
    },
  });
}

/**
 * Descarta el draft server-side (DELETE del row con completed_at IS NULL).
 */
export function useDiscardEnterpriseDraft() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (organizationId: string) => {
      if (!user) return;
      await supabase
        .from('enterprise_diagnostics')
        .delete()
        .eq('organization_id', organizationId)
        .eq('assessed_by', user.id)
        .is('completed_at', null);
    },
    onSuccess: (_data, organizationId) => {
      queryClient.invalidateQueries({ queryKey: ['enterprise-diagnostic-draft', organizationId] });
    },
  });
}

/**
 * Autosave server-side. Debounce corto (1.5s) + flush en beforeunload + flush
 * cuando el user pausa >1.5s. Corre en paralelo al localStorage.
 *
 * NOTA: el debounce del localStorage es 500ms, pero el server tiene un costo
 * de red por save. 1.5s balancea entre no perder respuestas y no saturar.
 * El flush en beforeunload usa la última copia de paramsRef (siempre fresca).
 */
export function useEnterpriseDraftAutosave(
  enabled: boolean,
  params: SaveDraftParams | null,
  debounceMs = 1500,
) {
  const saveDraft = useSaveEnterpriseDraft();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const paramsRef = useRef<SaveDraftParams | null>(null);
  const enabledRef = useRef<boolean>(false);
  paramsRef.current = params;
  enabledRef.current = enabled;

  useEffect(() => {
    if (!enabled || !params || !params.organizationId) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const current = paramsRef.current;
      if (!current || !current.organizationId) return;
      saveDraft.mutate(current);
    }, debounceMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, JSON.stringify(params), debounceMs]);

  // Flush en unload: si el user cierra la pestaña con cambios pendientes,
  // dispara un save sincrónico best-effort para no perderlos.
  useEffect(() => {
    const flush = () => {
      if (!enabledRef.current) return;
      const current = paramsRef.current;
      if (!current || !current.organizationId) return;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      saveDraft.mutate(current);
    };
    window.addEventListener('beforeunload', flush);
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush();
    });
    return () => {
      window.removeEventListener('beforeunload', flush);
      window.removeEventListener('pagehide', flush);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

interface AIEvaluationResult {
  dimensions: {
    dimensionId: DimensionId;
    name: string;
    score: number;
    subDimensionScores: SubDimensionScore[];
  }[];
}

export function useEvaluateDiagnostic() {
  return useMutation({
    mutationFn: async (params: {
      answers: Record<string, EnterpriseDiagnosticAnswer>;
      companyName: string;
      sector: string;
    }): Promise<AIEvaluationResult> => {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error('Not authenticated');

      // Fetch rubrics overrides if any
      const { data: rubricsRow } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('key', 'diagnostic_rubrics_override')
        .maybeSingle();

      const overrides = rubricsRow?.value as unknown as RubricsOverride | undefined;
      const rubricContext = buildRubricContext(
        overrides && Object.keys(overrides).length > 0 ? overrides : undefined
      );

      const { data, error } = await supabase.functions.invoke('evaluate-diagnostic', {
        body: {
          answers: params.answers,
          company_name: params.companyName,
          sector: params.sector,
          rubric_context: rubricContext,
        },
      });

      if (error) {
        // Intentamos extraer el mensaje en lenguaje natural devuelto por la función
        let body: any = null;
        try {
          const ctx: any = (error as any).context;
          if (ctx?.json) {
            body = await ctx.json();
          } else if (ctx instanceof Response) {
            body = await ctx.clone().json();
          } else if (ctx?.body) {
            body = typeof ctx.body === 'string' ? JSON.parse(ctx.body) : ctx.body;
          }
        } catch { /* ignore */ }

        let message = 'No pudimos evaluar el diagnóstico en este momento. Inténtalo de nuevo.';
        if (body?.error && !/non-?2xx/i.test(body.error)) message = body.error;
        throw new Error(message);
      }
      return data as AIEvaluationResult;
    },
  });
}
